import { Role, RoleType } from "@the-real-insight/tri-model";
import { useEffect, useRef, useState, memo } from "react";

import { useTranslation } from "react-i18next";
import { useDrag } from "react-dnd";
import { ChevronRight, Loader2, Trash2 } from "lucide-react";
import TreeView, { flattenTree, INode, ITreeViewProps } from 'react-accessible-treeview';
import { userAPI } from "../UserAPI";
import { useAtom } from "jotai";
import { authenticationAtom } from '../State';
import { useRoleAccess } from "../hooks/useRoleAccess";
import { useModalContext } from "./ModalProvider";
import { isOwnRole } from "../roleUtils";
import RoleLabel from "./RoleLabel";
import { parseRoles } from "../roleUtils";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Properties {
    className?: string;
    showParameterizedRoles?: boolean;
    showDelegatedRoles?: boolean;
    showIssuer?: boolean;
    customerName?: string;
    /** Called after a role is deleted (e.g. to refresh user list when role assignments were purged) */
    onRoleDeleted?: () => void | Promise<void>;
    /** Increment to reload the role list (e.g. after creating a role outside this component). */
    refreshSignal?: number;
}

/**
 * Kopfzeile einer Rollengruppe. `expandedByDefault` sagt, ob die Gruppe beim ersten Anzeigen
 * aufgeklappt ist — nur die eigenen Rollen sind es; System-, Standort-/Projekt- und Legacy-Rollen
 * stehen eingeklappt darunter, weil sie selten gebraucht werden und die Liste sonst lang wird.
 */
export interface GroupHeader {
    isGroupHeader: true;
    expandedByDefault?: boolean;
}

interface TreeNode {
    name: string;
    children: TreeNode[];
    metadata: Role | GroupHeader;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const SCOPE_ORDER = ['Site', 'Project'] as const;

const roleNode = (role: Role): TreeNode => ({ name: role.name, children: [], metadata: role });

const sortByName = (a: Role, b: Role) => a.getRoleName().localeCompare(b.getRoleName());

const sortScopes = (scopes: string[]) =>
    scopes.sort((a, b) => {
        const idxA = SCOPE_ORDER.indexOf(a as typeof SCOPE_ORDER[number]);
        const idxB = SCOPE_ORDER.indexOf(b as typeof SCOPE_ORDER[number]);
        const orderA = idxA >= 0 ? idxA : SCOPE_ORDER.length;
        const orderB = idxB >= 0 ? idxB : SCOPE_ORDER.length;
        return orderA !== orderB ? orderA - orderB : a.localeCompare(b);
    });

// ─── Draggable ───────────────────────────────────────────────────────────────

const Draggable = memo(function Draggable({ children, item, type }: {
    children: React.ReactNode;
    item: Role;
    type: string;
}) {
    const [{ isDragging }, drag, dragPreview] = useDrag(() => ({
        type, item,
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }), [type, item]);

    return (
        <div ref={dragPreview} className={isDragging ? "opacity-50" : undefined}>
            <div ref={drag}>{children}</div>
        </div>
    );
});

// ─── Tree building ───────────────────────────────────────────────────────────

function classifyRoles(
    roles: Role[],
    showDelegated: boolean,
    customerId: string,
    includeLegacyPlainRoles: boolean = false,
) {
    const customerRoles: Role[] = [];
    const ownRoles: Role[] = [];
    const legacyRoles: Role[] = [];
    const scopedGroups = new Map<string, Role[]>();

    for (const role of roles) {
        if (!role?.name) continue;
        if (role.type === RoleType.partnerDelegation && !showDelegated) continue;

        if (role.type === RoleType.role) {
            // Same predicate the tender context filters by, so the Teamverwaltung tree and the
            // tender surfaces cannot disagree about what an "eigene Rolle" is.
            if (isOwnRole(role, customerId)) {
                ownRoles.push(role);
            } else if (includeLegacyPlainRoles) {
                legacyRoles.push(role);
            }
        } else if (role.type === RoleType.parameterizableRole) {
            const scope = role.parameterizableBy?.[0];
            if (scope === 'Customer') {
                customerRoles.push(role);
            } else if (scope && Array.isArray(role.instances) && role.instances.length > 0) {
                if (!scopedGroups.has(scope)) scopedGroups.set(scope, []);
                scopedGroups.get(scope)!.push(role);
            }
        }
    }

    return { customerRoles, ownRoles, legacyRoles, scopedGroups };
}

function buildCustomerNodes(roles: Role[]): TreeNode[] {
    return roles
        .sort((a, b) => {
            if (a.getRoleID() === 'customerAdministrator') return -1;
            if (b.getRoleID() === 'customerAdministrator') return 1;
            return sortByName(a, b);
        })
        .map(roleNode);
}

function buildInstanceNodes(role: Role, showDelegates: boolean): TreeNode[] {
    if (!Array.isArray(role.instances)) return [];

    return role.instances
        .filter((inst) => inst?.name)
        .map((instance) => {
            instance.template = role as any;
            const node = roleNode(instance);

            if (showDelegates && Array.isArray(instance.delegates)) {
                for (const delegate of instance.delegates) {
                    if (!delegate?.name) continue;
                    delegate.principal = instance as any;
                    node.children.push(roleNode(delegate));
                }
            }
            return node;
        });
}

function buildScopedGroups(
    scopedGroups: Map<string, Role[]>,
    scopeLabels: Record<string, string>,
    showParameterized: boolean,
    showDelegated: boolean,
): TreeNode[] {
    return sortScopes(Array.from(scopedGroups.keys())).map((scope) => {
        const groupChildren: TreeNode[] = [];

        for (const role of scopedGroups.get(scope)!.sort(sortByName)) {
            if (showParameterized && Array.isArray(role.instances) && role.instances.length > 0) {
                groupChildren.push(...buildInstanceNodes(role, showDelegated));
            } else {
                groupChildren.push(roleNode(role));
            }
        }

        return {
            name: scopeLabels[scope] || scope,
            children: groupChildren,
            metadata: { isGroupHeader: true } as const,
        };
    });
}

export function buildTree(
    roles: Role[],
    showDelegated: boolean,
    showParameterized: boolean,
    scopeLabels: Record<string, string>,
    systemRolesLabel: string,
    ownRolesLabel: string,
    legacyLabel: string,
    customerId: string,
    includeLegacyPlainRoles: boolean = false,
): INode[] {
    if (!roles.length) return [];

    const { customerRoles, ownRoles, legacyRoles, scopedGroups } = classifyRoles(
        roles,
        showDelegated,
        customerId,
        includeLegacyPlainRoles,
    );

    const children: TreeNode[] = [];

    // Die eigenen Rollen zuerst und aufgeklappt: Sie sind das, was ein Kunde in der
    // Teamverwaltung pflegt und vergibt. Alles andere folgt eingeklappt darunter.
    if (ownRoles.length > 0) {
        children.push({
            name: ownRolesLabel,
            children: ownRoles.sort(sortByName).map(roleNode),
            metadata: { isGroupHeader: true, expandedByDefault: true },
        });
    }

    // Wrap the customer-scoped system roles in a collapsible group header, the
    // same way own/scoped/legacy roles are — otherwise they render flat and
    // cannot be collapsed.
    if (customerRoles.length > 0) {
        children.push({
            name: systemRolesLabel,
            children: buildCustomerNodes(customerRoles),
            metadata: { isGroupHeader: true },
        });
    }

    children.push(...buildScopedGroups(scopedGroups, scopeLabels, showParameterized, showDelegated));

    if (legacyRoles.length > 0) {
        children.push({
            name: legacyLabel,
            children: legacyRoles.sort(sortByName).map(roleNode),
            metadata: { isGroupHeader: true },
        });
    }

    if (!children.length) return [];
    return flattenTree({ name: 'Roles', children: children as any });
}

/**
 * Welche Gruppen beim ersten Anzeigen offen sind: die mit `expandedByDefault` — also die eigenen
 * Rollen. Hat der Kunde keine eigenen, wäre die Liste nur aus eingeklappten Köpfen; dann ist die
 * erste Gruppe offen, damit etwas zu sehen ist.
 */
export function initiallyExpandedGroupIds(treeData: INode[]): (number | string)[] {
    const header = (n: INode) => n.metadata as unknown as GroupHeader | undefined;
    const headers = treeData.filter((n) => header(n)?.isGroupHeader);
    const preferred = headers.filter((n) => header(n)?.expandedByDefault);
    return (preferred.length > 0 ? preferred : headers.slice(0, 1)).map((n) => n.id);
}

// ─── Component ───────────────────────────────────────────────────────────────

const refreshRoles = async (customerId: string, setRoles: (r: Role[]) => void) => {
    const data = await userAPI.getRoles(customerId);
    setRoles(parseRoles(data));
};

export default function RoleList(properties: Properties) {
    const { t } = useTranslation();
    const [authentication] = useAtom(authenticationAtom) as any;
    const roleAccess = useRoleAccess();
    const { showDeleteDialog, pushError } = useModalContext() as any;
    const [roles, setRoles] = useState<Role[]>([]);
    const [expandedIds, setExpandedIds] = useState<(number | string)[]>([]);
    const [loading, setLoading] = useState(true);
    const hasInitialExpanded = useRef(false);

    useEffect(() => {
        let cancelled = false;
        userAPI.getRoles(authentication.customer)
            .then((data) => {
                if (!cancelled) setRoles(parseRoles(data));
            })
            .catch((e) => { console.error('Error loading roles:', e); if (!cancelled) setRoles([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [authentication.customer, properties.refreshSignal]);

    const scopeLabels: Record<string, string> = {
        Site: t('settingsPanel.userManagementPanel.scopeSite'),
        Project: t('settingsPanel.userManagementPanel.scopeProject'),
        WorkingGroup: t('settingsPanel.userManagementPanel.scopeWorkingGroup'),
    };

    const treeData = buildTree(
        roles,
        !!properties.showDelegatedRoles,
        !!properties.showParameterizedRoles,
        scopeLabels,
        t('settingsPanel.userManagementPanel.scopeSystemRoles'),
        t('settingsPanel.userManagementPanel.scopeOwnRoles'),
        t('settingsPanel.userManagementPanel.scopeLegacy'),
        authentication.customer,
        roleAccess.isSystemAdmin,
    );

    const groupHeaderIds = treeData.filter((n) => n.metadata?.isGroupHeader).map((n) => n.id);
    const initialExpandedIds = initiallyExpandedGroupIds(treeData);

    useEffect(() => {
        if (!loading && groupHeaderIds.length > 0 && !hasInitialExpanded.current) {
            hasInitialExpanded.current = true;
            setExpandedIds(initialExpandedIds);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, groupHeaderIds.length]);

    const nodeRenderer: ITreeViewProps['nodeRenderer'] = ({
        element, isBranch, isExpanded, isDisabled, getNodeProps, level, handleExpand,
    }) => {
        if (element.metadata?.isGroupHeader) {
            return (
                <div
                    {...getNodeProps({ onClick: handleExpand })}
                    className={cn(
                        "mb-1 flex cursor-pointer items-center rounded px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground transition-colors hover:bg-muted",
                        level > 1 ? "mt-2" : "mt-4",
                        isDisabled && "opacity-50",
                    )}
                    style={{ marginLeft: 8 * (level - 1) }}
                >
                    <ChevronRight
                        aria-hidden="true"
                        className={cn(
                            "h-4 w-4 text-muted-foreground transition-transform",
                            isExpanded && "rotate-90",
                        )}
                    />
                    <span className="ml-0.5">{element.name}</span>
                </div>
            );
        }

        const role = element.metadata as unknown as Role;
        const roleName = role?.getRoleName?.() ?? (role as any)?.name ?? '';
        const isOwnRole = (role as any)?._tenant === authentication.customer;

        return (
            <div
                {...getNodeProps({ onClick: handleExpand })}
                className={cn(
                    "flex items-center py-[3px] transition-opacity",
                    isDisabled && "opacity-40",
                )}
                style={{ marginLeft: 12 * (level - 1) }}
            >
                {isBranch && (
                    <div className="flex w-4 shrink-0 items-center justify-center">
                        <ChevronRight
                            aria-hidden="true"
                            className={cn(
                                "h-3.5 w-3.5 text-muted-foreground/60 transition-transform",
                                isExpanded && "rotate-90",
                            )}
                        />
                    </div>
                )}
                <div className="flex min-w-0 flex-1 items-center">
                    <Draggable type="role" item={role}>
                        <RoleLabel
                            role={new Role(element.metadata)}
                            showIssuer={properties.showIssuer}
                            showDescription
                            customerName={properties.customerName}
                        />
                    </Draggable>
                </div>
                {isOwnRole && roleAccess.canManageRoles && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="ml-auto text-destructive hover:text-destructive"
                        title={t('settingsPanel.userManagementPanel.deleteOwnRole')}
                        aria-label={t('settingsPanel.userManagementPanel.deleteOwnRole')}
                        onClick={(e) => {
                            e.stopPropagation();
                            showDeleteDialog(
                                t('settingsPanel.userManagementPanel.deleteOwnRoleTitle'),
                                roleName,
                                async () => {
                                    try {
                                        await userAPI.deleteRole(authentication.customer, role);
                                        await refreshRoles(authentication.customer, setRoles);
                                        await properties.onRoleDeleted?.();
                                    } catch (err) {
                                        pushError(err);
                                    }
                                },
                            );
                        }}
                    >
                        <Trash2 aria-hidden="true" className="h-4 w-4" />
                    </Button>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className={cn("flex justify-center py-6", properties.className)}>
                <Loader2 aria-hidden="true" className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">{`${t('global.loading')} ...`}</span>
            </div>
        );
    }

    return (
        <div className={cn("py-1", properties.className)}>
            {treeData.length > 0 ? (
                <TreeView
                    data={treeData}
                    aria-label="Controlled expanded node tree"
                    expandedIds={expandedIds}
                    defaultExpandedIds={initialExpandedIds}
                    onExpand={({ treeState }) => setExpandedIds(Array.from(treeState.expandedIds))}
                    nodeRenderer={nodeRenderer}
                />
            ) : (
                <div className="p-4 text-center text-muted-foreground">
                    {t('settingsPanel.userManagementPanel.noRolesAvailable')}
                </div>
            )}
        </div>
    );
}
